import {
  useEffect,
  useMemo,
  useState
} from "react";

import {
  useParams
} from "react-router-dom";

import {
  motion,
  AnimatePresence
} from "framer-motion";

import {
  getProductsByCollectionSlug
} from "../services/productService";

import {
  getCollectionBySlug
} from "../services/collectionService";

import ProductCard
  from "../components/ProductCard";

import SkeletonProduct
  from "../components/SkeletonProduct";

function CollectionProducts() {

  const { slug } =
    useParams();

  const [products, setProducts] =
    useState([]);

  const [collection, setCollection] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [filters, setFilters] =
    useState({

      category: "All",

      sort: "latest",

      maxPrice: 100000

    });

  /* ===============================
     LOAD PRODUCTS
  =============================== */

  useEffect(() => {

    const loadProducts =
      async () => {

      try {

        setLoading(true);

        /* ===============================
           COLLECTION
        =============================== */

        const collectionData =
          await getCollectionBySlug(slug);

        setCollection(
          collectionData
        );

        /* ===============================
           PRODUCTS
        =============================== */

        const productData =
          await getProductsByCollectionSlug(
            slug
          );

        console.log(
          "COLLECTION SLUG:",
          slug
        );

        console.log(
          "PRODUCTS:",
          productData
        );

        setProducts(

          Array.isArray(productData)

            ? productData

            : []

        );

      } catch (err) {

        console.error(err);

        setProducts([]);

      } finally {

        setLoading(false);

      }

    };

    loadProducts();

  }, [slug]);

  /* ===============================
     CATEGORIES
  =============================== */

  const categories =
    useMemo(() => {

      const unique = [

        ...new Set(

          products
            .map((p) =>
              p.category
            )
            .filter(Boolean)

        )

      ];

      return [
        "All",
        ...unique
      ];

    }, [products]);

  /* ===============================
     FILTER PRODUCTS
  =============================== */

  const filteredProducts =
    useMemo(() => {

      let filtered =
        [...products];

      /* CATEGORY */

      if (
        filters.category !== "All"
      ) {

        filtered =
          filtered.filter(

            product =>

              product.category
              ===
              filters.category

          );

      }

      /* PRICE */

      filtered =
        filtered.filter(

          product =>

            Number(product.price)
            <=
            filters.maxPrice

        );

      /* SORT */

      switch (filters.sort) {

        case "priceLow":

          filtered.sort(
            (a, b) =>
              a.price - b.price
          );

          break;

        case "priceHigh":

          filtered.sort(
            (a, b) =>
              b.price - a.price
          );

          break;

        case "name":

          filtered.sort(
            (a, b) =>

              a.name.localeCompare(
                b.name
              )

          );

          break;

        default:

          filtered.reverse();

      }

      return filtered;

    }, [products, filters]);

  return (

    <div className="
      min-h-screen
      bg-[#FDFCFB]
    ">

      {/* HERO */}

      <section className="
        relative
        overflow-hidden
        h-[45vh]
        flex items-center justify-center
      ">

        <div className="absolute inset-0">

          <img
            src="/og-banner.jpg"
            alt="collection"
            className="
              w-full
              h-full
              object-cover
            "
          />

          <div className="
            absolute inset-0
            bg-black/50
          " />

        </div>

        <div className="
          relative z-20
          text-center
          px-6
        ">

          <p className="
            text-white/70
            uppercase
            tracking-[0.4em]
            text-[10px]
            mb-5
          ">

            Curated Collection

          </p>

          <h1 className="
            text-white
            text-5xl
            md:text-7xl
            font-serif
            italic
            mb-4
          ">

            {collection?.name ||
              "Sacred Collection"}

          </h1>

        </div>

      </section>

      {/* MAIN */}

      <div className="
        max-w-7xl
        mx-auto
        px-4 md:px-6
        py-16
      ">

        <div className="
          flex
          flex-col lg:flex-row
          gap-10
        ">

          {/* SIDEBAR */}

          <aside className="
            lg:w-72
            h-fit
            sticky top-28
            bg-white
            border border-stone-200
            rounded-[2rem]
            p-8
          ">

            <div className="
              mb-10
            ">

              <p className="
                text-[10px]
                uppercase
                tracking-[0.35em]
                text-stone-400
                mb-3
              ">

                Refine Collection

              </p>

              <h2 className="
                text-4xl
                font-serif
                italic
              ">

                Filters

              </h2>

            </div>

            {/* CATEGORY */}

            <div className="
              mb-10
            ">

              <h3 className="
                text-[10px]
                uppercase
                tracking-[0.35em]
                text-stone-400
                mb-5
              ">

                Category

              </h3>

              <div className="
                flex flex-wrap
                gap-3
              ">

                {categories.map((cat) => (

                  <button

                    key={cat}

                    onClick={() =>

                      setFilters({

                        ...filters,

                        category: cat

                      })

                    }

                    className={`
                      px-5 py-3
                      rounded-full
                      text-xs
                      uppercase
                      tracking-[0.25em]
                      transition-all

                      ${
                        filters.category === cat

                          ? `
                            bg-black
                            text-white
                          `

                          : `
                            border
                            border-stone-200
                            bg-white
                          `
                      }
                    `}
                  >

                    {cat}

                  </button>

                ))}

              </div>

            </div>

            {/* PRICE */}

            <div className="
              mb-10
            ">

              <div className="
                flex justify-between
                mb-4
              ">

                <h3 className="
                  text-[10px]
                  uppercase
                  tracking-[0.35em]
                  text-stone-400
                ">

                  Max Price

                </h3>

                <span className="
                  text-sm
                ">

                  ₹{filters.maxPrice}

                </span>

              </div>

              <input
                type="range"
                min="500"
                max="100000"
                step="500"
                value={filters.maxPrice}
                onChange={(e) =>

                  setFilters({

                    ...filters,

                    maxPrice:
                      Number(
                        e.target.value
                      )

                  })

                }

                className="
                  w-full
                  accent-black
                "
              />

            </div>

            {/* SORT */}

            <div>

              <h3 className="
                text-[10px]
                uppercase
                tracking-[0.35em]
                text-stone-400
                mb-5
              ">

                Sort By

              </h3>

              <select

                value={filters.sort}

                onChange={(e) =>

                  setFilters({

                    ...filters,

                    sort:
                      e.target.value

                  })

                }

                className="
                  w-full
                  border
                  border-stone-200
                  rounded-full
                  px-5 py-4
                  bg-white
                  text-sm
                  outline-none
                "
              >

                <option value="latest">
                  Latest
                </option>

                <option value="priceLow">
                  Price: Low to High
                </option>

                <option value="priceHigh">
                  Price: High to Low
                </option>

                <option value="name">
                  Name
                </option>

              </select>

            </div>

          </aside>

          {/* PRODUCTS */}

          <div className="
            flex-1
          ">

            <div className="
              mb-10
            ">

              <h2 className="
                text-3xl
                font-serif
                italic
                mb-2
              ">

                Explore Collection

              </h2>

              <p className="
                text-stone-500
              ">

                {filteredProducts.length}
                {" "}
                sacred artworks available

              </p>

            </div>

            {loading ? (

              <div className="
                grid
                grid-cols-1
                sm:grid-cols-2
                xl:grid-cols-3
                gap-8
              ">

                {[1,2,3,4,5,6].map((i) => (

                  <SkeletonProduct
                    key={i}
                  />

                ))}

              </div>

            ) : (

              <>

                {filteredProducts.length === 0 ? (

                  <div className="
                    text-center
                    py-24
                  ">

                    <h2 className="
                      text-4xl
                      font-serif
                      italic
                      mb-4
                    ">

                      No Products Found

                    </h2>

                  </div>

                ) : (

                  <div className="
                    grid
                    grid-cols-1
                    sm:grid-cols-2
                    xl:grid-cols-3
                    gap-8
                  ">

                    <AnimatePresence>

                      {filteredProducts.map(
                        (product, index) => (

                        <motion.div

                          key={product.id}

                          initial={{
                            opacity: 0,
                            y: 40
                          }}

                          animate={{
                            opacity: 1,
                            y: 0
                          }}

                          transition={{
                            delay:
                              index * 0.05
                          }}
                        >

                          <ProductCard
                            product={product}
                          />

                        </motion.div>

                      ))}

                    </AnimatePresence>

                  </div>

                )}

              </>

            )}

          </div>

        </div>

      </div>

    </div>

  );

}

export default CollectionProducts;